import { useState, useEffect } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import { useAnalysis } from '../../contexts/AnalysisContext';
import { extractVideoId } from '../../utils/linkUtils';
import './Ideas.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

const normalizeAnalysisMarkdown = (value) => {
    if (!value) return '';

    let normalized = String(value).replace(/\r\n?/g, '\n').trim();

    normalized = normalized
        .replace(/^\s*```(?:markdown|md)?[ \t]*\n/i, '')
        .replace(/\n\s*```[ \t]*$/i, '')
        .replace(/^[ \t]*#{1,6}[ \t]*$/gm, '')
        .replace(/[ \t]+#{3,}[ \t]*(?=\n|$)/g, '')
        .replace(/^(?:(?:certainly|sure|of course)[!.]?\s+)?here(?:'s| are)[^\n]*:\s*/i, '')
        .replace(/(^|\n)[ \t]*(\d+)[.)][ \t]*(?:\n[ \t]*)+/g, '$1$2. ')
        .replace(/(^|\n)[ \t]*(\d+)[.)][ \t]+/g, '$1$2. ')
        .replace(/^(?:\d+\.\s*)?\*\*([^*]+)\*\*\s*:/gm, '### $1\n')
        .replace(/(:[ \t]*)-[ \t]+/g, '$1\n- ')
        .replace(/(^|\n)[ \t]+-[ \t]+/g, '$1- ')
        .replace(/([.!?])[ \t]+-[ \t]+/g, '$1\n- ')
        .replace(/[ \t]+-[ \t]+(\*\*[^*]+\*\*:)/g, '\n- $1')
        .replace(/[ \t]+\*[ \t]+\*/g, '\n\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();

    return normalized;
};

const analysisMarkdownComponents = {
    h1: ({ node, children, ...props }) => (
        <h3 className="analysis-heading analysis-heading-primary" {...props}>{children}</h3>
    ),
    h2: ({ node, children, ...props }) => (
        <h3 className="analysis-heading analysis-heading-primary" {...props}>{children}</h3>
    ),
    h3: ({ node, children, ...props }) => (
        <h4 className="analysis-heading" {...props}>{children}</h4>
    ),
    h4: ({ node, children, ...props }) => (
        <h4 className="analysis-heading" {...props}>{children}</h4>
    ),
    p: ({ node, children, ...props }) => (
        <p className="analysis-paragraph" {...props}>{children}</p>
    ),
    ul: ({ node, children, ...props }) => (
        <ul className="analysis-list" {...props}>{children}</ul>
    ),
    ol: ({ node, children, ...props }) => (
        <ol className="analysis-list analysis-list-ordered" {...props}>{children}</ol>
    ),
    li: ({ node, children, ...props }) => (
        <li className="analysis-list-item" {...props}>{children}</li>
    ),
    blockquote: ({ node, children, ...props }) => (
        <blockquote className="analysis-quote" {...props}>{children}</blockquote>
    ),
    strong: ({ node, children, ...props }) => (
        <strong className="analysis-strong" {...props}>{children}</strong>
    ),
    em: ({ node, children, ...props }) => (
        <em className="analysis-emphasis" {...props}>{children}</em>
    ),
    a: ({ node, children, href, ...props }) => (
        <a
            className="analysis-link"
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            {...props}
        >
            {children}
        </a>
    ),
    code: ({ node, inline, className, children, ...props }) => {
        const code = String(children).replace(/\n$/, '');

        if (inline) {
            return <code className="analysis-inline-code" {...props}>{children}</code>;
        }

        return (
            <pre className="analysis-code-block">
                <code className={className || 'analysis-code'} {...props}>{code}</code>
            </pre>
        );
    }
};

const AnalysisMarkdown = ({ content }) => (
    <div className="analysis-markdown">
        <ReactMarkdown skipHtml components={analysisMarkdownComponents}>
            {normalizeAnalysisMarkdown(content)}
        </ReactMarkdown>
    </div>
);

const Ideas = ({ isProAccount, openBillingPortal }) => {
    const { username, youtubeName } = useRevit();
    const { getToken } = useAuth();
    const { analysis, setAnalysis } = useAnalysis();
    const [isLoading, setIsLoading] = useState(false);
    const [timeframe, setTimeframe] = useState('30days');
    const [activeTab, setActiveTab] = useState('conversion');
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    // Add states for comments analysis
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [commentsAnalysis, setCommentsAnalysis] = useState(null);
    const [latestVideos, setLatestVideos] = useState([]);
    const [isLoadingVideos, setIsLoadingVideos] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');
    const [isAnalyzingTranscript, setIsAnalyzingTranscript] = useState(false);
    const [videoTranscriptAnalysis, setVideoTranscriptAnalysis] = useState(null);

    // Add useEffect to fetch latest videos
    useEffect(() => {
        const fetchLatestVideos = async () => {
            if (!username || !youtubeName) return;

            setIsLoadingVideos(true);
            try {
                const token = await getToken();
                const response = await axios.get(`https://${SERVER_URL}/api/get-latest-videos/${username}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                setLatestVideos(response.data.videos);
            } catch (error) {
                console.error('Error fetching latest videos:', error);
            } finally {
                setIsLoadingVideos(false);
            }
        };

        fetchLatestVideos();
    }, [username, youtubeName, getToken, SERVER_URL]);

    // Add function to analyze comments
    const analyzeComments = async () => {
        if (!latestVideos || latestVideos.length === 0) {
            alert('No videos available to analyze');
            return;
        }
        
        const videoIds = latestVideos.slice(0, 5).map(video => video.id);
        setIsLoadingComments(true);
        setCommentsAnalysis(null);
        
        try {
            const token = await getToken();
            const response = await axios.get(
                `https://${SERVER_URL}/api/youtube-comments`,
                {
                    params: {
                        username: username,
                        videoIds: videoIds
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            
            if (response.data.success) {
                setCommentsAnalysis(response.data.analysis);
            } else {
                console.error('Error analyzing comments:', response.data.error);
                alert('Error analyzing comments');
            }
        } catch (error) {
            console.error('Error analyzing comments:', error);
            alert('Error analyzing comments');
        } finally {
            setIsLoadingComments(false);
        }
    };

    const getGptVideoAnalysis = async () => {
        if (!username) return;
        setIsLoading(true);
        
        try {
            const token = await getToken();
            
            // Calculate dates based on timeframe
            const now = new Date();
            let startDate = new Date(now);
            
            switch(timeframe) {
                case '7days':
                    startDate.setDate(now.getDate() - 7);
                    break;
                case '30days':
                    startDate.setDate(now.getDate() - 30);
                    break;
                case '90days':
                    startDate.setDate(now.getDate() - 90);
                    break;
                case 'alltime':
                    startDate = new Date(2020, 0, 1); // Set to a far past date
                    break;
                default:
                    startDate.setDate(now.getDate() - 30);
            }
            
            const response = await axios.get(
                `https://${SERVER_URL}/api/gpt-video-analysis/${username}`,
                {
                    params: {
                        startDate: startDate.toISOString(),
                        endDate: now.toISOString()
                    },
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setAnalysis(response.data.gptResponse);
        } catch (error) {
            console.error('Error getting GPT video analysis:', error);
            alert('Error getting video analysis. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    };

    const analyzeVideoTranscript = async () => {
        const videoId = extractVideoId(videoUrl.trim());
        if (!videoId) {
            alert('Please provide a valid YouTube video URL.');
            return;
        }

        setIsAnalyzingTranscript(true);
        setVideoTranscriptAnalysis(null);

        try {
            const token = await getToken();
            const response = await axios.post(
                `https://${SERVER_URL}/api/video-transcript-analysis/${username}`,
                {
                    videoId: videoId.trim()
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            setVideoTranscriptAnalysis(response.data.analysis);
        } catch (error) {
            console.error('Error analyzing video transcript:', error);
            alert(error.response?.data?.message || error.response?.data?.error || 'Error analyzing video transcript. Please try again later.');
        } finally {
            setIsAnalyzingTranscript(false);
        }
    };

    // Function to format the analysis text with proper styling
    const formatAnalysis = (text) => {
        if (!text) return null;

        // Models often wrap headings in **bold** or # markdown. Strip that before
        // locating "Step N:" so a leading "**" is not treated as its own section.
        const cleaned = String(text)
            .replace(/\r\n?/g, '\n')
            .replace(/^\s*```(?:markdown|md)?[ \t]*\n/i, '')
            .replace(/\n\s*```[ \t]*$/i, '')
            .replace(/\*\*/g, '')
            .replace(/^#{1,6}[ \t]+/gm, '')
            .trim();

        const stepMatches = [...cleaned.matchAll(/Step\s+(\d+)\s*:\s*([^\n]*)\n?([\s\S]*?)(?=\n[ \t]*Step\s+\d+\s*:|$)/gi)];

        if (stepMatches.length < 3) {
            return <AnalysisMarkdown content={text} />;
        }

        const cleanLine = (line) => line
            .replace(/^[-*•][ \t]+/, '')
            .replace(/^["'](.+)["']$/, '$1')
            .replace(/^[*_]+|[*_]+$/g, '')
            .trim();

        const sections = stepMatches.slice(0, 3).map((match) => {
            let title = cleanLine(match[2] || '');
            const bodyLines = match[3]
                .split('\n')
                .map((line) => cleanLine(line))
                .filter(Boolean);

            if (!title && bodyLines.length) {
                title = bodyLines.shift();
            }

            return { title, bodyLines };
        });

        const [{ title: patternTitle, bodyLines: patternItems }, { title: painPointTitle, bodyLines: painPointContent }, { title: ideasTitle, bodyLines: ideaItems }] = sections;

        return (
            <div className="analysis-content">
                <div className="analysis-section">
                    <h3>Step 1: {patternTitle}</h3>
                    <div className="pattern-items">
                        {patternItems.map((pattern, index) => (
                            <div key={`pattern-${index}`} className="pattern-item">{pattern}</div>
                        ))}
                    </div>
                </div>
                
                <div className="analysis-section">
                    <h3>Step 2: {painPointTitle}</h3>
                    <div className="pain-point-content">
                        {painPointContent.map((paragraph, index) => (
                            <p key={`pain-${index}`}>{paragraph}</p>
                        ))}
                    </div>
                </div>
                
                <div className="analysis-section">
                    <h3>Step 3: {ideasTitle}</h3>
                    <div className="idea-items">
                        {ideaItems.map((idea, index) => (
                            <div key={`idea-${index}`} className="idea-item">{idea}</div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="ideas-container">
            <div className="youtube-account-container">
                <p className="body-large-semibold">AI Insights</p>
            </div>
            <div className="ideas-header body-small-regular">
                <p>Get AI-powered insights and ideas based on your past click & conversion data. Also get AI to analyzie your comments so you don't have to read them.</p>
            </div>

            <div className="ideas-tabs" role="tablist" aria-label="AI Insights tools">
                <button
                    id="conversion-tab"
                    className={`ideas-tab${activeTab === 'conversion' ? ' active' : ''}`}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'conversion'}
                    aria-controls="conversion-panel"
                    onClick={() => setActiveTab('conversion')}
                >
                    Video Conversion Analyzer
                </button>
                <button
                    id="performance-tab"
                    className={`ideas-tab${activeTab === 'performance' ? ' active' : ''}`}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'performance'}
                    aria-controls="performance-panel"
                    onClick={() => setActiveTab('performance')}
                >
                    Video Performance Analysis
                </button>
                <button
                    id="comments-tab"
                    className={`ideas-tab${activeTab === 'comments' ? ' active' : ''}`}
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'comments'}
                    aria-controls="comments-panel"
                    onClick={() => setActiveTab('comments')}
                >
                    Comments Analysis
                </button>
            </div>

            {activeTab === 'conversion' && (
            <div
                id="conversion-panel"
                className="analysis-card video-conversion-analyzer"
                role="tabpanel"
                aria-labelledby="conversion-tab"
            >
                <div className="card-header">
                    <div className="header-left">
                        <h2 className="heading-h3">Video Conversion Analyzer</h2>
                        <p className="video-conversion-description">
                            Enter a high-converting YouTube video URL to uncover its hooks, messaging, and calls to action.
                        </p>
                    </div>
                </div>

                <div className="video-conversion-fields">
                    <div className="video-conversion-field">
                        <div className="transcript-label-row">
                            <label htmlFor="video-url">YouTube video URL</label>
                        </div>
                        <input
                            id="video-url"
                            type="url"
                            value={videoUrl}
                            onChange={(event) => setVideoUrl(event.target.value)}
                            placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                        />
                    </div>
                </div>

                <div className="video-conversion-footer">
                    <p>We’ll fetch the captions and identify the messaging and conversion patterns that made this video perform well.</p>
                    <button
                        className="analyze-button"
                        type="button"
                        onClick={analyzeVideoTranscript}
                        disabled={isAnalyzingTranscript}
                    >
                        {isAnalyzingTranscript ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Analyzing video...
                            </>
                        ) : (
                            'Analyze video'
                        )}
                    </button>
                </div>

                {videoTranscriptAnalysis && (
                    <div
                        className="analysis-results conversion-analysis-results"
                        aria-live="polite"
                        aria-busy={isAnalyzingTranscript}
                    >
                        <AnalysisMarkdown content={videoTranscriptAnalysis} />
                    </div>
                )}
            </div>
            )}

            {activeTab === 'performance' && (
            <div
                id="performance-panel"
                className="analysis-card"
                role="tabpanel"
                aria-labelledby="performance-tab"
            >
                <div className="card-header">
                    <div className="header-left">
                        <h2 className="heading-h3">Video Performance Analysis</h2>
                        <div className="analysis-timeframe-selector">
                            <label htmlFor="timeframe">Time Period:</label>
                            <select
                                id="timeframe"
                                value={timeframe}
                                onChange={(e) => setTimeframe(e.target.value)}
                            >
                                <option value="7days">Last 7 Days</option>
                                <option value="30days">Last 30 Days</option>
                                <option value="90days">Last 90 Days</option>
                                <option value="alltime">All Time</option>
                            </select>
                        </div>
                    </div>
                    <button 
                        className="analyze-button" 
                        onClick={getGptVideoAnalysis} 
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Analyzing your data...
                            </>
                        ) : (
                            'Analyze My Video Data'
                        )}
                    </button>
                </div>

                {analysis ? (
                    <div className="analysis-results">
                        {formatAnalysis(analysis)}
                    </div>
                ) : (
                    <div className="no-analysis">
                        {isLoading ? (
                            <>
                                <p>AI is analyzing your video performance data...</p>
                                <p>This may take a few minutes. Please do not close this page.</p>
                            </>
                        ) : (
                            <p>Click the button above to let AI find patterns from the highest converting videos and get AI-generated content ideas.</p>
                        )}
                    </div>
                )}
            </div>
            )}

            {activeTab === 'comments' && (
            <div
                id="comments-panel"
                className="analysis-card video-conversion-analyzer"
                role="tabpanel"
                aria-labelledby="comments-tab"
            >
                <div className={`comments-analysis-container ${!commentsAnalysis ? 'empty' : ''}`}>
                    <div className="card-header">
                        <div className="header-left-comments">
                            <h2 className="heading-h3">Comments Analysis</h2>
                            {!isProAccount && <span className="pro-badge-analysis">PRO</span>}
                        </div>
                        {youtubeName && latestVideos.length > 0 && !isLoadingVideos && (
                            <button
                                className="analyze-button"
                                onClick={analyzeComments}
                                disabled={isLoadingComments || !isProAccount}
                            >
                                {isLoadingComments ? (
                                    <>
                                        <FontAwesomeIcon icon={faSpinner} spin /> Analyzing comments...
                                    </>
                                ) : (
                                    <>
                                        Analyze Comments
                                        {!isProAccount && <span className="pro-badge">PRO</span>}
                                    </>
                                )}
                            </button>
                        )}
                    </div>

                    {commentsAnalysis ? (
                        <div className="analysis-results conversion-analysis-results">
                            <AnalysisMarkdown content={commentsAnalysis} />
                        </div>
                    ) : (
                        <div className="no-analysis">
                            {isLoadingVideos ? (
                                <>
                                    <p><FontAwesomeIcon icon={faSpinner} spin /> Loading your latest videos...</p>
                                </>
                            ) : isLoadingComments ? (
                                <>
                                    <p>AI is analyzing your video comments...</p>
                                    <p>This may take a few minutes. Please do not close this page.</p>
                                </>
                            ) : (
                                <>
                                    <p>Analyze comments from your latest videos to get insights about viewer sentiment, questions, and feedback.</p>
                                    {!isProAccount && <p className="upgrade-prompt"><button onClick={() => openBillingPortal()} className="upgrade-prompt">Upgrade your account to access this feature.</button></p>}
                                </>
                            )}
                            {!youtubeName && <p>Please connect your YouTube account to use this feature.</p>}
                        </div>
                    )}
                </div>
            </div>
            )}
        </div>
    );
};

export default Ideas;
